'use client';

import { useState, useRef, useEffect } from 'react';
import { Search, SlidersHorizontal, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProjectControlsProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedTech: string;
  setSelectedTech: (tech: string) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
  availableTechs: string[];
  searchPlaceholder?: string;
}

export default function ProjectControls({
  searchQuery,
  setSearchQuery,
  selectedTech,
  setSelectedTech,
  sortBy,
  setSortBy,
  availableTechs,
  searchPlaceholder = 'Search...',
}: ProjectControlsProps) {
  const [isTechOpen, setIsTechOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const techRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (techRef.current && !techRef.current.contains(event.target as Node))
        setIsTechOpen(false);
      if (sortRef.current && !sortRef.current.contains(event.target as Node))
        setIsSortOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-2 sm:gap-3 lg:gap-4 w-full mb-6 sm:mb-8 animate-reveal [animation-delay:100ms] relative z-[60]">
      <div className="relative flex-grow group">
        <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-muted-foreground group-focus-within:text-primary transition-colors">
          <Search className="w-4 h-4" />
        </div>
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full h-12 pl-12 pr-4 rounded-xl glass-dark border border-white/5 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all outline-none text-sm placeholder:text-muted-foreground/50"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:gap-4 w-full lg:w-auto">
        {/* Custom Tech Select */}
        <div className="relative w-full lg:w-auto flex-1" ref={techRef}>
          <button
            onClick={() => setIsTechOpen(!isTechOpen)}
            className="h-12 px-5 rounded-xl glass-dark border border-white/5 hover:border-primary/50 transition-all flex items-center gap-3 text-sm w-full lg:min-w-[200px] justify-between group cursor-pointer"
          >
            <span
              className={cn(
                selectedTech === 'All'
                  ? 'text-muted-foreground'
                  : 'text-foreground font-medium',
              )}
            >
              {selectedTech === 'All' ? 'All Technologies' : selectedTech}
            </span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform duration-300',
                isTechOpen && 'rotate-180',
              )}
            />
          </button>

          {isTechOpen && (
            <div className="absolute top-full left-0 lg:right-0 mt-2 w-full lg:w-64 bg-[#0a0a0a] border border-white/10 rounded-2xl p-2 shadow-2xl z-50 overflow-hidden animate-reveal origin-top">
              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-1">
                <button
                  onClick={() => {
                    setSelectedTech('All');
                    setIsTechOpen(false);
                  }}
                  className={cn(
                    'w-full px-4 py-2.5 rounded-lg text-left text-sm transition-all flex items-center justify-between group cursor-pointer',
                    selectedTech === 'All'
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-white/5 text-muted-foreground hover:text-foreground',
                  )}
                >
                  All Technologies
                  {selectedTech === 'All' && <Check className="w-4 h-4" />}
                </button>
                {availableTechs.map((tech) => (
                  <button
                    key={tech}
                    onClick={() => {
                      setSelectedTech(tech);
                      setIsTechOpen(false);
                    }}
                    className={cn(
                      'w-full px-4 py-2.5 rounded-lg text-left text-sm transition-all flex items-center justify-between group cursor-pointer',
                      selectedTech === tech
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-white/5 text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tech}
                    {selectedTech === tech && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Custom Sort Select */}
        <div className="relative w-full lg:w-auto flex-1" ref={sortRef}>
          <button
            onClick={() => setIsSortOpen(!isSortOpen)}
            className="h-12 px-5 rounded-xl glass-dark border border-white/5 hover:border-primary/50 transition-all flex items-center gap-3 text-sm w-full lg:min-w-[160px] justify-between group cursor-pointer"
          >
            <span className="text-foreground font-medium">
              {sortBy === 'newest' ? 'Newest First' : 'A-Z'}
            </span>
            <SlidersHorizontal className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </button>

          {isSortOpen && (
            <div className="absolute top-full left-0 lg:right-0 mt-2 w-full lg:w-48 bg-[#0a0a0a] border border-white/10 rounded-2xl p-2 shadow-2xl z-50 animate-reveal origin-top">
              <button
                onClick={() => {
                  setSortBy('newest');
                  setIsSortOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-2.5 rounded-lg text-left text-sm transition-all flex items-center justify-between cursor-pointer',
                  sortBy === 'newest'
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-white/5 text-muted-foreground hover:text-foreground',
                )}
              >
                Newest First
                {sortBy === 'newest' && <Check className="w-4 h-4" />}
              </button>
              <button
                onClick={() => {
                  setSortBy('alphabetical');
                  setIsSortOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-2.5 rounded-lg text-left text-sm transition-all flex items-center justify-between cursor-pointer',
                  sortBy === 'alphabetical'
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-white/5 text-muted-foreground hover:text-foreground',
                )}
              >
                Alphabetical (A-Z)
                {sortBy === 'alphabetical' && <Check className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
